import { Button, Descriptions, Form, Grid, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../../../../../constants';
import {
  Entity,
  SendReportCaseV1,
  SendReportCaseV1CaseDetailsApplicationInput,
  SendReportCaseV1RevisionRequest,
  SendReportCaseV1RevisionRequestRequestChanges
} from '../../../../../../../../generated';
import { useMemo, useReducer } from 'react';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface CaseDetailsApplicationProps {
  data: SendReportCaseV1;
  onSave: (value: SendReportCaseV1CaseDetailsApplicationInput) => void;
  onSaveAndComplete: (value: SendReportCaseV1CaseDetailsApplicationInput) => void;
  entities: Entity[];
  revisionRequested: SendReportCaseV1RevisionRequest;
}

export const CaseDetailsApplication: React.FC<CaseDetailsApplicationProps> = (props) => {
  const labelStyle = {
    fontSize: '12px'
  };

  const descriptionStyle = {
    paddingLeft: '8px'
  };
  
  interface ApplicationFormState {
    isApplicationEditable: boolean;
    isSendDestinationEditable: boolean;
    requestedChanges: SendReportCaseV1RevisionRequestRequestChanges;
  }

  const initialStage: ApplicationFormState = {
    isApplicationEditable: false,
    isSendDestinationEditable: false,
    requestedChanges: {
      requestUpdatedDestination: props.revisionRequested?.requestedChanges?.requestUpdatedDestination
    }
  };

  const reducer = (state: ApplicationFormState, action: { type: string }) => {
    switch (action.type) {
      case 'edit-destination':
        return {
          ...state,
          isApplicationEditable: true,
          isSendDestinationEditable: true
        };
      case 'cancel':
        return {
          isSendDestinationEditable: false,
          isApplicationEditable: false,
          requestedChanges: { ...props.revisionRequested.requestedChanges }
        };
      case 'save':
        return { ...initialStage };
      case 'save-complete':
        return { ...initialStage };
      default:
        return state;
    }
  };
  const [form] = Form.useForm();
  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;
  const handleSave = () => {
    form.validateFields().then(async (values) => {
      await props.onSave(values);
      dispatch({ type: 'save' });
    });
  };

  const handleSaveAndComplete = () => {
    form.validateFields().then(async (values) => {
      await props.onSaveAndComplete(values);
      dispatch({ type: 'save-complete' });
    });
  };

  const initialValues = {
    destination: props.data.caseDetails?.application?.destination?.id
  };

  const clientEntities = useMemo(() => props.entities.filter((entity) => entity.isClient), [props.entities]);

  const [applicationFormState, dispatch] = useReducer(reducer, initialStage);
  return (
    <>
      <Descriptions labelStyle={labelStyle} layout="vertical" column={isMobile ? 1 : 3}>
        <Descriptions.Item label="Submission Date">
          {props.data.submittedAt
            ? dayjs(props.data.submittedAt ?? '')
                .utc()
                .format(DATE_FORMAT)
                .toUpperCase()
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Decision Date">{props.data.caseDetails?.applicationReview?.decision?.completedAt ? dayjs(props.data.caseDetails?.applicationReview?.decision?.completedAt).utc().format(DATE_FORMAT) : "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Case Id">{props.data.id}</Descriptions.Item>
        <Descriptions.Item label="Decision">{props.data.caseDetails?.applicationReview?.decision?.result ?? 'Pending'}</Descriptions.Item>
      </Descriptions>

      <Descriptions
        title={<span style={{ whiteSpace: 'normal' }}>Credential for which you want to send a Verification Status Report:</span>}
        labelStyle={labelStyle}
        layout="vertical"
      >
        <Descriptions.Item>
          <Typography.Text strong style={descriptionStyle}>
            {`${
              props.data.caseDetails?.application?.credential?.caseDetails?.application?.issuingInstitution?.entityName ??
              props.data.caseDetails?.application?.credential?.caseDetails?.application?.otherIssuingInstitution?.institutionName
            } - ${props.data.caseDetails?.application?.credential?.caseDetails?.application?.credentialTitle}`}
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>
      {!applicationFormState.isSendDestinationEditable && (
        <Descriptions
          title="Recipient for the Verification Status Report:"
          labelStyle={labelStyle}
          layout="vertical"
          extra={
            <Button type="primary" disabled={!applicationFormState.requestedChanges.requestUpdatedDestination} onClick={() => dispatch({ type: 'edit-destination' })}>
              Edit
            </Button>
          }
          column={1}
        >
          <Descriptions.Item style={descriptionStyle}>{props.data.caseDetails?.application?.destination?.entityName ?? 'N/A'}</Descriptions.Item>
        </Descriptions>
      )}
      {applicationFormState.isSendDestinationEditable && (
        <>
          <Typography.Text style={{ fontSize: '15px', fontWeight: 400, marginBottom: '8px' }}>Recipient for the Verification Status Report:</Typography.Text>
          <Form layout="vertical" style={{ width: '50%' }} form={form} initialValues={initialValues}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Please select a Destination.'
                }
              ]}
              name={['destination']}
            >
              <Select placeholder="Select Destination" style={{ width: 300 }}>
                {clientEntities.map((entity) => (
                  <Select.Option key={entity.id} value={entity.id}>
                    {entity.entityName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </>
      )}
      <div
        style={{
          width: '85%',
          display: applicationFormState.isApplicationEditable ? 'flex' : 'none',
          justifyContent: 'space-between'
        }}
      >
        <Button
          onClick={() => {
            dispatch({ type: 'cancel' });
          }}
        >
          Cancel
        </Button>
        <div
          style={{
            display: 'flex',
            gap: '8px'
          }}
        >
          <Button onClick={handleSave}>Save</Button>
          <Button type="primary" onClick={handleSaveAndComplete}>
            Save and Complete
          </Button>
        </div>
      </div>
    </>
  );
};
